import {
	STUDENT_ONBOARDING_PREFERENCE_KEYS,
	isValidInstrumentValue,
	isValidLevelValue,
	isValidStartWhenValue,
} from './student-onboarding-options';

const STUDENT_ONBOARDING_PREFILL_STORAGE_KEY = 'student_onboarding_prefill_search';

const { instrument: INSTRUMENT_KEY, level: LEVEL_KEY, daysType: DAYS_TYPE_KEY, timeOfDay: TIME_OF_DAY_KEY } = STUDENT_ONBOARDING_PREFERENCE_KEYS;

const safeJsonParse = (value) => {
	if (!value || typeof value !== 'string') return null;

	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

const normalizeScalar = (value) => {
	if (typeof value !== 'string') return value;

	const trimmedValue = value.trim();

	if (!trimmedValue) return null;
	if (/^\d+$/.test(trimmedValue)) return Number(trimmedValue);

	return trimmedValue;
};

const normalizeList = (value) => {
	if (!value) return [];

	const values = Array.isArray(value) ? value : [value];

	return values
		.flatMap((item) => {
			if (Array.isArray(item)) return item;

			const parsedValue = safeJsonParse(item);

			if (Array.isArray(parsedValue)) return parsedValue;
			if (typeof item === 'string' && item.includes(',')) {
				return item.split(',');
			}

			return [item];
		})
		.map(normalizeScalar)
		.filter(Boolean);
};

const getParamValues = (searchParams, keys) => (
	keys.flatMap((key) => searchParams.getAll(key)).filter(Boolean)
);

const getFirstParamValue = (searchParams, keys) => {
	for (const key of keys) {
		const value = searchParams.get(key);

		if (value) return value;
	}

	return null;
};

export const parseStudentOnboardingPrefill = (search = '') => {
	const searchParams = new URLSearchParams(search);
	const rawInstrumentValues = normalizeList(
		getParamValues(searchParams, ['instrument', 'instrument_id', 'instruments'])
	);
	const instrumentIds = rawInstrumentValues.filter((item) => typeof item === 'number');
	const instrumentValue = rawInstrumentValues.find(
		(item) => typeof item === 'string' && isValidInstrumentValue(item)
	);

	const selectedTeacherId = normalizeScalar(
		getFirstParamValue(searchParams, ['teacher', 'teacher_id', 'teacher_slug'])
	);

	const preferencesPayload = (
		getFirstParamValue(searchParams, ['goals', 'student_preferences'])
	);
	const parsedPreferences = safeJsonParse(preferencesPayload);
	const studentPreferences = {};

	if (parsedPreferences && typeof parsedPreferences === 'object' && !Array.isArray(parsedPreferences)) {
		Object.assign(studentPreferences, parsedPreferences);
	}

	const levelValue = normalizeScalar(getFirstParamValue(searchParams, ['level', 'current_level']));
	const startWhenRaw = getFirstParamValue(searchParams, ['start_when']);
	const startWhenValue = startWhenRaw ? safeJsonParse(startWhenRaw) : null;

	if (instrumentValue) studentPreferences[INSTRUMENT_KEY] = instrumentValue;
	if (
		levelValue &&
		isValidLevelValue({
			instrument: studentPreferences[INSTRUMENT_KEY],
			level: levelValue,
		})
	) {
		studentPreferences[LEVEL_KEY] = levelValue;
	}
	if (startWhenValue && isValidStartWhenValue(startWhenValue)) {
		studentPreferences[DAYS_TYPE_KEY] = startWhenValue.days_type;
		studentPreferences[TIME_OF_DAY_KEY] = startWhenValue.time_of_day;
	}

	const hasStudentPreferences = Object.keys(studentPreferences).length > 0;
	const hasInstrument = !!studentPreferences[INSTRUMENT_KEY] || instrumentIds.length > 0;
	const hasLevel = !!studentPreferences[LEVEL_KEY];
	const hasDaysType = Array.isArray(studentPreferences[DAYS_TYPE_KEY]) && studentPreferences[DAYS_TYPE_KEY].length > 0;
	const hasTimeOfDay = Array.isArray(studentPreferences[TIME_OF_DAY_KEY]) && studentPreferences[TIME_OF_DAY_KEY].length > 0;
	const hasCompleteStudentPreferences = hasInstrument && hasLevel && hasDaysType && hasTimeOfDay;
	const hasRelevantParams = instrumentIds.length > 0 || hasStudentPreferences || !!selectedTeacherId;

	return {
		instrumentIds,
		selectedTeacherId: selectedTeacherId || null,
		studentPreferences,
		hasStudentPreferences,
		hasCompleteStudentPreferences,
		hasRelevantParams,
		initialStep: hasCompleteStudentPreferences ? 3 : hasLevel ? 2 : hasInstrument ? 1 : 0,
	};
};

export const getStudentOnboardingPrefill = ({ search = '', persistedSearch = '' } = {}) => {
	const persistedPrefill = parseStudentOnboardingPrefill(persistedSearch);
	const searchPrefill = parseStudentOnboardingPrefill(search);

	const instrumentIds = (
		searchPrefill.instrumentIds.length > 0 ? searchPrefill.instrumentIds : persistedPrefill.instrumentIds
	);
	const selectedTeacherId = searchPrefill.selectedTeacherId || persistedPrefill.selectedTeacherId || null;
	const studentPreferences = {
		...persistedPrefill.studentPreferences,
		...searchPrefill.studentPreferences,
	};
	const hasStudentPreferences = Object.keys(studentPreferences).length > 0;
	const hasInstrument = !!studentPreferences[INSTRUMENT_KEY] || instrumentIds.length > 0;
	const hasLevel = !!studentPreferences[LEVEL_KEY];
	const hasDaysType = Array.isArray(studentPreferences[DAYS_TYPE_KEY]) && studentPreferences[DAYS_TYPE_KEY].length > 0;
	const hasTimeOfDay = Array.isArray(studentPreferences[TIME_OF_DAY_KEY]) && studentPreferences[TIME_OF_DAY_KEY].length > 0;
	const hasCompleteStudentPreferences = hasInstrument && hasLevel && hasDaysType && hasTimeOfDay;
	const hasRelevantParams = instrumentIds.length > 0 || hasStudentPreferences || !!selectedTeacherId;

	return {
		instrumentIds,
		selectedTeacherId,
		studentPreferences,
		hasStudentPreferences,
		hasCompleteStudentPreferences,
		hasRelevantParams,
		initialStep: hasCompleteStudentPreferences ? 3 : hasLevel ? 2 : hasInstrument ? 1 : 0,
	};
};

export const persistStudentOnboardingPrefillSearch = (search = '') => {
	const parsedPrefill = parseStudentOnboardingPrefill(search);

	if (!parsedPrefill.hasRelevantParams) {
		localStorage.removeItem(STUDENT_ONBOARDING_PREFILL_STORAGE_KEY);
		return;
	}

	localStorage.setItem(STUDENT_ONBOARDING_PREFILL_STORAGE_KEY, search);
};

export const clearStudentOnboardingPrefillSearch = () => {
	localStorage.removeItem(STUDENT_ONBOARDING_PREFILL_STORAGE_KEY);
};

export const getPersistedStudentOnboardingPrefillSearch = () => (
	localStorage.getItem(STUDENT_ONBOARDING_PREFILL_STORAGE_KEY) || ''
);

export const areSameIdLists = (left = [], right = []) => {
	const normalizedLeft = [...left].map(normalizeScalar).filter(Boolean).sort();
	const normalizedRight = [...right].map(normalizeScalar).filter(Boolean).sort();

	return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};

export const areSameStudentPreferences = (left = {}, right = {}) => (
	JSON.stringify(left || {}) === JSON.stringify(right || {})
);
